import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { COMPANY_FAQ_CATEGORIES } from "@/content/company-corpus/faq-defs";

export const Route = createFileRoute("/en/company/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/company-corpus");
    return { faqs: m.COMPANY_FAQ_EN };
  },
  component: CompanyFaqHubEn,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Company Registration in Thailand — FAQ (${faqs.length} Answers)`,
        description:
          "Thailand company registration FAQ for foreign shareholders — 49% ownership limits and nominee risk, Foreign Business Licence, US Treaty of Amity, BOI promotion, registered capital versus work permit and visa, VAT registration, audited accounts and document legalisation. Referenced to dbd.go.th, boi.go.th and rd.go.th.",
        path: "/en/company/faq",
        type: "article",
        alternates: [{ lang: "th", path: "/company/faq" }],
        keywords: [
          "company registration thailand",
          "foreign business licence",
          "treaty of amity",
          "BOI promotion",
          "thai nominee shareholder",
          "registered capital work permit",
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Company Registration FAQ", url: `${site}/en/company/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/company/faq")) },
      ],
    };
  },
});

function CompanyFaqHubEn() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Company Registration FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Company Registration and Foreign Business Licensing in Thailand — {faqs.length} FAQs
        </h1>
        <p className="mb-6 text-muted-foreground">
          Answers follow the Civil and Commercial Code, the Foreign Business Act B.E. 2542 (1999) and published
          guidance from the Department of Business Development (dbd.go.th), the Board of Investment (boi.go.th),
          the Revenue Department (rd.go.th) and the Department of Employment (doe.go.th).
        </p>
        <div className="mb-8 rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
          <strong className="text-foreground">Please note:</strong> holding shares through Thai shareholders who do
          not genuinely invest (nominee structures) is an offence under the Foreign Business Act B.E. 2542. We do not
          set up nominee structures. Licence approvals and BOI incentives are at each authority's discretion, and
          official fees and timelines may change — reconfirm before you file.
        </div>

        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>ภาษาไทย:</strong>{" "}
          <a href="/company/faq" className="text-primary underline">
            FAQ จดทะเบียนบริษัทสำหรับคนต่างชาติ →
          </a>
        </div>

        <section aria-label="Company FAQ topics" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">Browse by topic</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {COMPANY_FAQ_CATEGORIES.map((c) => (
              <a
                key={c.slug}
                href={`/en/company/faq/${c.slug}`}
                className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm"
              >
                <div className="mb-1 font-semibold">{c.titleEn}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descEn}</div>
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">Q{i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <section className="mt-12">
          <h2 className="mb-3 text-xl font-bold">Related services</h2>
          <ul className="grid gap-2 text-sm md:grid-cols-2">
            <li><a className="text-primary underline" href="/en/work-permit/type">Work permit routes</a></li>
            <li><a className="text-primary underline" href="/en/visa/thai/non-b">Non-B business visa</a></li>
            <li><a className="text-primary underline" href="/en/translation/faq">Certified translation of corporate documents</a></li>
            <li><a className="text-primary underline" href="/en/notary/faq">Notarial certification for overseas use</a></li>
          </ul>
        </section>

        <section className="mt-12 rounded-lg border bg-primary/5 p-6">
          <h2 className="mb-2 text-xl font-bold">Still need an answer?</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Ask our team about shareholding structure, required documents and scope of work by phone, LINE or email —
            Mon-Sat 9:00-18:00.
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 font-medium text-primary-foreground">
            Contact the team →
          </a>
        </section>

        <FaqSourceNote topic="company" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}